import { useState } from "react";
import Papa from "papaparse";
import { UploadCloud, BookOpen, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function UploadSection({ onUpload, savedSession, onResume }) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  const processFile = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        const toRead = data.filter((row) => row["Exclusive Shelf"] === "to-read");
        const validBooks = toRead.length > 0
          ? toRead
          : data.filter((row) => row["Title"] && row["Author"]);

        if (validBooks.length === 0) {
          setError("Could not find valid book data in this CSV.");
          return;
        }
        mapAndReturn(validBooks);
      },
      error: (err) => setError(err.message),
    });
  };

  const normalizeKey = (title, author) =>
    `${title.toLowerCase().replace(/[^a-z0-9]/g, "")}|${author.toLowerCase().replace(/[^a-z0-9]/g, "")}`;

  const mapAndReturn = (rows) => {
    const seen = new Set();
    const parsed = [];
    for (const row of rows) {
      let isbn = row["ISBN13"] || row["ISBN"] || "";
      isbn = isbn.replace(/[^0-9]/g, "");
      const title = row["Title"] || "Unknown Title";
      const author = row["Author"] || "Unknown Author";
      const key = normalizeKey(title, author);
      if (seen.has(key)) continue;
      seen.add(key);
      parsed.push({ title, author, isbn: isbn || undefined });
    }
    onUpload(parsed);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);
    const file = e.dataTransfer.files[0];
    if (file && (file.type === "text/csv" || file.name.endsWith(".csv"))) {
      processFile(file);
    } else {
      setError("Please upload a valid CSV file.");
    }
  };
  const handleFileChange = (e) => {
    setError(null);
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {savedSession && (
        <div
          className="flex items-center justify-between gap-4 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 shadow-sm"
          data-testid="banner-resume"
        >
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                You have saved results from {formatDate(savedSession.checkedAt)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {savedSession.results.length} of {savedSession.booksToSearch.length} books checked
              </p>
            </div>
          </div>
          <Button size="sm" onClick={onResume} className="shrink-0 gap-1.5" data-testid="button-resume">
            Resume
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      <Card className="shadow-md border-border/50">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-serif">Upload your Wishlist</CardTitle>
          <CardDescription className="text-base">
            Export your library from Goodreads and upload the CSV here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`mt-6 border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/50"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="dropzone"
          >
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full text-primary">
                <UploadCloud className="w-8 h-8" />
              </div>
            </div>
            <p className="text-lg font-medium text-foreground mb-2">Drag &amp; drop your CSV file here</p>
            <p className="text-sm text-muted-foreground mb-6">or click to browse your files</p>
            <label htmlFor="csv-upload">
              <Button asChild className="cursor-pointer">
                <span data-testid="button-select-file">Select File</span>
              </Button>
            </label>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileChange}
              data-testid="input-csv-upload"
            />
          </div>

          {error && (
            <div className="mt-4 p-4 bg-destructive/10 text-destructive text-sm rounded-md text-center" data-testid="error-upload">
              {error}
            </div>
          )}

          <div className="mt-8 text-center border-t border-border pt-6">
            <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center justify-center gap-2">
              <BookOpen className="w-4 h-4 text-muted-foreground" />
              How to get your Goodreads export
            </h3>
            <ol className="text-sm text-muted-foreground space-y-1 inline-block text-left">
              <li>1. Go to Goodreads and click "My Books"</li>
              <li>2. Click "Import and export" in the left sidebar</li>
              <li>3. Click the "Export Library" button at the top</li>
              <li>4. Wait for the export to generate, then download it</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
